import { cache } from 'react';
import { ISbComponentType } from 'storyblok-js-client';

import { GET_STORY_EMPTY_RESPONSE, STORY_VERSIONS } from '@monoimin/constants';

import { ServerTiming } from '../debug';

import { newStoryblokApi } from './newStoryblokApi';
import { defineMergedParams } from './defineMergedParams';
import { GetStoryParams, ISbStoryCustom } from './types';

class StoryblokError extends Error {
  constructor(
    message: string,
    public status: number,
    public slug: string,
    public params?: GetStoryParams
  ) {
    super(message);
    this.name = 'StoryblokError';
  }
}

const getStoryData = async <T = ISbComponentType<string>>(
  slug: string,
  params?: GetStoryParams,
  serverTiming?: ServerTiming
): Promise<ISbStoryCustom<T>> => {
  serverTiming?.lap('getStory');
  const mergedParams = (await defineMergedParams(params)) as GetStoryParams;
  const isDraft = mergedParams.version === STORY_VERSIONS.DRAFT;

  const { Storyblok } = newStoryblokApi(isDraft, 'server');
  try {
    const response = await Storyblok.getStory(slug, mergedParams);
    serverTiming?.lap('getStory');
    return response as unknown as ISbStoryCustom<T>;
  } catch (error: any) {
    if (error?.status === 404) {
      return {
        ...GET_STORY_EMPTY_RESPONSE,
        error: new StoryblokError('Story not found', 404, slug, mergedParams),
      } as ISbStoryCustom<T>;
    }

    return {
      ...GET_STORY_EMPTY_RESPONSE,
      error: new StoryblokError(
        error?.message || 'Unknown error',
        error?.status || 500,
        slug,
        mergedParams
      ),
    } as ISbStoryCustom<T>;
  }
};

export const getStory = cache(getStoryData);
